import Box from '@mui/material/Box';
import { type BoxProps } from '@mui/material/Box';
import { useCalloutStyles } from './callout.styles';
import type { ReactNode, JSX } from 'react';
import { BodyS, Caption } from '../../../typography';
import { Status } from '../status';
import { Icon } from '@doxyme/icons';

type CalloutIconProps = {
  level: CalloutProps['level'];
  color?: string;
  size?: number;
};

const CalloutIcon = ({ level, color, size }: CalloutIconProps): JSX.Element => {
  switch (level) {
    case 'success': {
      return <Status statusLevel='success' size={size} />;
    }
    case 'informative': {
      return <Status statusLevel='info' size={size} />;
    }
    case 'notice': {
      return <Status statusLevel='warning' size={size} />;
    }
    case 'error': {
      return <Status statusLevel='error' size={size} />;
    }
    default: {
      return <Icon.Message size={size} color={color} css={{ flexShrink: 0 }} />;
    }
  }
};

export type CalloutProps = BoxProps & {
  level?: 'neutral' | 'error' | 'notice' | 'informative' | 'success';
  showIcon?: boolean;
  size?: 'small' | 'default';
  title?: string;
  children?: ReactNode | string;
};

type ContainerProps = {
  children: ReactNode;
  size: CalloutProps['size'];
  title?: string;
  styles: ReturnType<typeof useCalloutStyles>;
};

const Container = ({ children, size, title, styles }: ContainerProps) => {
  if (size === 'small' && Boolean(title)) {
    return <Box css={styles.textWrapper}>{children}</Box>;
  }
  return <>{children}</>;
};

export const Callout = ({
  level = 'neutral',
  showIcon = true,
  size = 'default',
  title,
  children,
  ...rest
}: CalloutProps): JSX.Element | null => {
  if (!Boolean(children)) {
    return null;
  }

  const styles = useCalloutStyles({ level, size });

  return (
    <Box role='status' css={styles.container} {...rest}>
      {showIcon && (
        <CalloutIcon
          level={level}
          color={styles.iconColor}
          size={size === 'small' ? 20 : 24}
        />
      )}

      <Container size={size} title={title} styles={styles}>
        {Boolean(title) && (
          <Caption fontWeight='medium' css={styles.title}>
            {title}
          </Caption>
        )}

        {typeof children === 'string' ? (
          <BodyS css={styles.text}>{children}</BodyS>
        ) : (
          children
        )}
      </Container>
    </Box>
  );
};
